// Request/response shapes for routes Track 0 implements (auth + lookups),
// plus type stubs for the rest of the API surface so downstream tracks can
// import these and fill in the bodies.

import type {
  AccountLink,
  AccountLinkKind,
  AccountLinkStatus,
  OobEntry,
  AccountRow,
  AccountStatus,
  AccountStatusEventRow,
  AccountTypeRow,
  AuditAction,
  AuditLogRow,
  BalanceRow,
  BalanceSource,
  FrtCategoryRow,
  FrtCustomerRow,
  MonthlySnapshotRow,
  NotificationKind,
  NotificationRow,
  ProductTypeRow,
  PublicUser,
  SnapshotAmendmentRow,
  SnapshotMetadata,
  StatusActiveSuppressed,
  TesterCategory,
  TesterKind,
  TesterRelationship,
  TesterRow,
  W9Status,
  TillerMappingHistoryRow,
  TillerStatus,
  TransactionRow,
  UploadRow,
  UserRole,
  UserStatus,
} from './entities.js';
import type { DateOnly, ISO8601, Paginated, Period } from './primitives.js';
import type { CustomerProfileRow, ProfileMode } from './profiles.js';

// ---- Auth ----

export interface LoginRequest {
  username: string;
  password: string;
}

export interface LoginResponse {
  user: PublicUser;
  must_reset: boolean;
}

export interface ChangePasswordRequest {
  current_password: string;
  new_password: string;
}

export interface MeResponse {
  user: PublicUser;
}

// ---- Lookups ----

export interface ProductTypeUpsert {
  code: string;
  name: string;
  active?: boolean;
}

export interface AccountTypeUpsert {
  code: string;
  name: string;
  active?: boolean;
}

export interface FrtCategoryUpsert {
  name: string;
  active?: boolean;
  is_customer?: boolean;
}

export interface OobTypeUpsert {
  name: string;
  contact_value: boolean;
  field_type: string;
  active?: boolean;
  sort_order?: number;
}

// ---- Track 1: FRT Customers ----

export interface FrtCustomerCreate {
  code: string;
  name: string;
  notes?: string | null;
  /** Platforms the customer offers, with their profile mode (mig 0017).
   *  Optional for the API (non-customer FIs have none); the New Customer
   *  form requires at least one. */
  platforms?: { platform_id: number; name?: string | null; profile_mode: ProfileMode }[];
  /** FRT Category id. Optional — defaults server-side to "Active Customers".
   *  Editable on the customer detail page after creation. */
  frt_category_id?: number;
}

export type FrtCustomerPatch = Partial<FrtCustomerCreate>;

export interface FrtCustomerListQuery {
  status?: StatusActiveSuppressed;
  frt_category_id?: number;
  /** true = FRT customers only, false = non-customer FIs only. */
  is_customer?: boolean;
  page?: number;
  per_page?: number;
}

export interface FrtCustomerListItem extends FrtCustomerRow {
  account_count: number;
  /** Joined from frt_category for convenience in list/detail views. */
  frt_category_name?: string | null;
  /** From the category (mig 0014): false for non-customer external FIs. */
  is_customer: boolean;
}

export type FrtCustomerDetail = FrtCustomerListItem;

export interface FrtCustomerSuppressRequest {
  reason?: string | null;
}

// Backwards-compat alias used by some Track 0 type stubs.
export type FrtCustomerUpsert = FrtCustomerCreate;

// ---- Track 1: Testers ----

export interface TesterCreate {
  kind: TesterKind;
  /** Tester Category — Third Party (W9 + contract + bank info admin applies)
   *  or Internal (FRT staff). Defaults to 'internal' if omitted. */
  tester_category?: TesterCategory;
  // person fields (required when kind === 'person', else NULL)
  first_name?: string | null;
  last_name?: string | null;
  short_name?: string | null;
  // business fields (required when kind === 'business', else NULL)
  business_name?: string | null;
  short_business_name?: string | null;
  opened_by_first_name?: string | null;
  opened_by_last_name?: string | null;
  opened_by_initials?: string | null;
  // status — FK to tester_status_option lookup
  tester_status_id?: number | null;
  // shared admin fields
  email?: string | null;
  mobile_phone?: string | null;
  mailing_address?: string | null;
  citizenship_id?: number | null;
  w9?: W9Status | null;
  bank_info_id?: number | null;
  bac_payee?: string | null;
  personal_bank_for_payment?: string | null;
  notes?: string | null;
}

export type TesterPatch = Partial<Omit<TesterCreate, 'kind'>>;

export interface TesterListQuery {
  kind?: TesterKind;
  tester_status_id?: number;
  tester_category?: TesterCategory;
  frt_customer_id?: number;
  page?: number;
  per_page?: number;
}

export interface TesterListItem extends TesterRow {
  account_count: number;
  /** Joined name from tester_status_option for convenience. */
  tester_status_name?: string | null;
}

export interface TesterDetail extends TesterRow {
  account_count: number;
  accounts: AccountRow[];
  tester_status_name?: string | null;
  bank_info_name?: string | null;
  citizenship_name?: string | null;
}

export interface TesterSuppressRequest {
  reason?: string | null;
}

// Backwards-compat alias used by some Track 0 type stubs.
export type TesterUpsert = TesterCreate;

// ---- Track 1: Accounts ----

export interface AccountCreate {
  legacy_account_id?: string | null;
  /** Customer profile the account rolls up to (mig 0017). Required unless
   *  an admin sets allow_incomplete_profile (imports). */
  customer_profile_id?: number | null;
  /** Taken from the profile when one is given (must match if sent). */
  frt_customer_id?: number;
  /** Taken from per-tester profiles; required for shared profiles. */
  tester_id?: number;
  account_type_id: number;
  product_type_id: number;
  accounts_held?: string | null;
  date_opened?: DateOnly | null;
  account_number_last4: string;
  debit_card_available?: boolean | null;
  checks_available?: boolean | null;
  tiller_status?: TillerStatus;
  tiller_account_id?: string | null;
  tiller_account_reference?: string | null;
  in_bitwarden?: boolean | null;
  frt_category_id?: number | null;
  on_point_tester_id?: number | null;
  account_status?: AccountStatus;
  notes?: string | null;
  // Tester Relationship + per-account contract terms (mig 0010).
  tester_relationship?: TesterRelationship;
  contract_signed?: boolean | null;
  contract_term_months?: number | null;
  contract_signed_date?: DateOnly | null;
  frt_tester_fee?: number | null;
  payment_status_id?: number | null;
  payment_date?: DateOnly | null;
  /** Admin-only escape hatch for bulk/migration imports: create the
   *  account without a customer profile. */
  allow_incomplete_profile?: boolean;
}

export type AccountPatch = Partial<AccountCreate>;

// ---- Linked accounts (mig 0014) ----

export type AccountLinkCreate =
  | { kind: 'frt'; linked_account_id: number; linked_on?: DateOnly }
  | {
      kind: 'external';
      frt_customer_id: number;
      product_type_id: number;
      last4: string;
      linked_on?: DateOnly;
    };

export interface AccountLinkUnlink {
  /** Defaults to today. Must not precede linked_on. */
  unlinked_on?: DateOnly;
}

export interface AccountLinksResponse {
  linked: AccountLink[];
  previously_linked: AccountLink[];
}

/** One row per link, for Excel pivots (Reports → Linked Accounts). */
export interface LinkedAccountsExportRow {
  link_id: number;
  account_id: number;
  account_display_name: string;
  account_frt_customer_code: string;
  kind: AccountLinkKind;
  linked_account_display_name: string | null;
  linked_bank_code: string;
  linked_bank_name: string;
  linked_bank_is_customer: boolean;
  linked_product_type: string;
  linked_last4: string;
  status: AccountLinkStatus;
  linked_on: DateOnly;
  unlinked_on: DateOnly | null;
}

export interface AccountListQuery {
  tiller_status?: TillerStatus;
  account_status?: AccountStatus;
  frt_customer_id?: number;
  tester_id?: number;
  frt_category_id?: number;
  on_point_tester_id?: number;
  /** Comma-separated account ids; restricts list to just these. Used by
   *  dashboard "Review" deep-links so users can identify the specific rows
   *  flagged by an alert. */
  ids?: string;
  q?: string; // full-text on display_name
  /** Online profile / platform type (mig 0017). */
  customer_profile_id?: number;
  platform_id?: number;
  /** Accounts on profiles using this phone (contact phone or OTP channel). */
  phone_number_id?: number;
  page?: number;
  per_page?: number;
}

export interface AccountListItem extends AccountRow {
  // Joined names so list pages don't need a second round trip
  frt_customer_code: string;
  frt_customer_name: string;
  tester_display: string; // person short_name or business short_business_name
  product_type_code: string;
  account_type_code: string;
  frt_category_name: string | null;
  on_point_tester_display: string | null;
  latest_balance_amount: number | null;
  latest_balance_as_of: string | null;
  /** Joined name from payment_status_option (e.g. "Closing", "Suspended") */
  payment_status_name: string | null;
  /** Computed: contract_signed_date + contract_term_months. Null when contract
   *  fields aren't populated. */
  contract_expiration_date: string | null;
  /** Latest balance as_of_date filtered by the account's expected source:
   *    tiller_status='manual'           → max(balance.as_of_date) for source='manual'
   *    integrated / login_issues / new  → max(balance.as_of_date) for source='tiller'
   *  Null when no matching balance row exists. The FE renders this as
   *  "Today" / "N days" with a green/orange/red background. */
  last_update_date: DateOnly | null;
  // ---- From the customer profile (mig 0017) ----
  /** Customer platform name (mig 0018), e.g. "Consumer Lending". */
  platform_name: string | null;
  /** Platform type, e.g. "Personal". */
  platform_type_name: string | null;
  /** "ALLY · Personal · JerryT" */
  customer_profile_display: string | null;
  phone_on_file_id: number | null;
  phone_on_file_country_code: string | null;
  phone_on_file_number: string | null;
  email_on_file: string | null;
  /** One line per OOB channel, e.g.
   *  "Phone SMS: +1 5551234567 [iPhone · FRT · JerryT]"; '; '-separated. */
  oob_summary: string | null;
  /** Active linked accounts, e.g. "CHASE Savings ••1234 (since 2026-03-01)"; '; '-separated. */
  linked_accounts_summary: string | null;
  /** Previously linked, e.g. "CHASE Savings ••1234 (2025-01-05 – 2026-02-01)". */
  previously_linked_summary: string | null;
}

export interface AccountDetail extends AccountListItem {
  frt_customer: FrtCustomerRow;
  tester: TesterRow;
  product_type: ProductTypeRow;
  account_type: AccountTypeRow;
  frt_category: FrtCategoryRow | null;
  on_point_tester: TesterRow | null;
  customer_profile: CustomerProfileRow | null;
  /** The profile's OTP channels. */
  oob_entries: OobEntry[];
}

export interface AccountSuppressRequest {
  reason?: string | null;
  account_status?: Extract<AccountStatus, 'suppressed' | 'closed' | 'converted_personal'>;
}

export interface AccountMoveRequest {
  reason?: string | null;
}

export interface AccountLinkTillerRequest {
  tiller_account_id: string;
  tiller_account_reference?: string | null;
  reason?: 'initial_link' | 'remap' | 'tiller_id_changed';
}

export interface AccountUnlinkTillerRequest {
  reason?: string | null;
}

export type AccountStatusEventListItem = AccountStatusEventRow;
export type AccountMappingHistoryItem = TillerMappingHistoryRow;

export interface AccountBalanceListQuery {
  page?: number;
  per_page?: number;
}

export interface AccountTransactionListQuery {
  from?: DateOnly;
  to?: DateOnly;
  page?: number;
  per_page?: number;
}

export interface AccountSnapshotListItem extends MonthlySnapshotRow {
  metadata: Record<string, unknown>;
}

export type AccountBalanceItem = BalanceRow;
export type AccountTransactionItem = TransactionRow;

// Backwards-compat alias used by some Track 0 type stubs.
export type AccountUpsert = AccountCreate;

// ---- Track 2: Uploads (Tiller ingest) ----

/** A single missing-app-account row in `parse_summary.missing_app_accounts`. */
export interface MissingAppAccount {
  account_id: number;
  display_name: string;
  tiller_account_id: string;
  tiller_account_reference: string | null;
  last_seen_in_upload_id: number | null;
}

/** A single unmatched-tiller-account row in `parse_summary.unmatched_tiller_accounts`. */
export interface UnmatchedTillerAccount {
  tiller_account_id: string;
  tiller_account_reference: string | null;
  account_number_last4: string | null;
  institution: string | null;
  latest_balance: number | null;
  latest_observed_at: ISO8601 | null;
}

/** Per-row error captured during parsing; included in `parse_summary.errors`. */
export interface UploadParseError {
  code: string;
  sheet?: string;
  row?: number;
  column?: string;
  message: string;
}

/** Structured form of `upload.parse_summary` (JSON-decoded). */
export interface UploadParseSummary {
  upload_id: number;
  rows_processed: { balances: number; transactions: number };
  balances_added: number;
  balances_skipped_dup: number;
  transactions_added: number;
  transactions_skipped_dup: number;
  accounts_seen: number;
  missing_app_accounts: MissingAppAccount[];
  unmatched_tiller_accounts: UnmatchedTillerAccount[];
  errors: UploadParseError[];
}

/**
 * Response of POST /uploads. Mirrors `upload` row plus the parsed parse_summary
 * so the FE can drive the resolutions modal without a second round trip.
 */
export interface UploadCreateResponse {
  upload: Omit<UploadRow, 'parse_summary'>;
  parse_summary: UploadParseSummary;
}

export interface UploadDetailResponse {
  upload: Omit<UploadRow, 'parse_summary'>;
  parse_summary: UploadParseSummary | null;
}

export interface UploadHistoryItem extends Omit<UploadRow, 'parse_summary'> {
  uploaded_by_username: string | null;
  parse_summary: UploadParseSummary | null;
}

export interface UploadConflictDetails {
  /** Sent in error envelope `details` when DUPLICATE_UPLOAD. */
  prior_upload_id: number;
}

// ---------------------------------------------------------------------------
// Chunked upload flow.
//
// Tiller workbooks grow without bound (one balance row per account per day).
// Server-side parse on Workers Free can't fit that in 10ms CPU. So:
//
//   1. POST /uploads/raw  — multipart xlsx → R2 archive, hash, dedup, returns
//                           an upload_id in parse_status='pending'.
//   2. GET  /uploads/sync-state — per-account latest dates so the browser can
//                           filter parsed rows down to the delta.
//   3. POST /uploads/:id/chunk — JSON body of (filtered) balance and
//                           transaction rows. Multiple chunks per upload.
//   4. POST /uploads/:id/finalize — set parse_status, compute the diff
//                           (missing/unmatched), return parse_summary.
//
// The legacy POST /uploads (multipart with full-file parse) is kept for
// small-file convenience and tests; large feeds use the chunked path.

export interface UploadRawResponse {
  upload_id: number;
  filename: string;
  file_hash: string;
  storage_key: string;
  uploaded_at: ISO8601;
  /** True if this hash was already known — server returns the *prior* upload's
   *  id so the FE can short-circuit and run resolutions on it instead of
   *  uploading again. */
  duplicate_of_prior?: number;
}

export interface UploadSyncStateAccount {
  tiller_account_id: string;
  /** Latest balance.observed_at the server has for this Tiller account. NULL
   *  if no balance row exists yet. Browser sends rows where observed_date is
   *  strictly greater. */
  latest_balance_observed_at: DateOnly | null;
  /** Latest transaction.posted_date for this Tiller account. */
  latest_transaction_posted_date: DateOnly | null;
}

export interface UploadSyncStateResponse {
  accounts: UploadSyncStateAccount[];
}

/** Single balance row sent to /uploads/:id/chunk. Mirrors the parser's
 *  per-row shape; parsing happens in the browser. */
export interface UploadChunkBalance {
  tiller_account_id: string;
  tiller_balance_id: string;
  observed_date: DateOnly;
  balance: number;
  account_number_last4?: string | null;
  institution?: string | null;
  account_status?: string | null;
  source?: string | null;
  tiller_account_reference?: string | null;
}

export interface UploadChunkTransaction {
  tiller_account_id: string;
  tiller_transaction_id: string;
  posted_date: DateOnly;
  amount: number;
  description?: string | null;
  full_description?: string | null;
  category?: string | null;
  category_hint?: string | null;
  check_number?: string | null;
  source?: string | null;
}

export interface UploadChunkRequest {
  balances?: UploadChunkBalance[];
  transactions?: UploadChunkTransaction[];
}

export interface UploadChunkResponse {
  balances_added: number;
  balances_skipped_dup: number;
  transactions_added: number;
  transactions_skipped_dup: number;
}

export interface UploadFinalizeRequest {
  /** Total rows the browser parsed before sync-state filtering. Used only
   *  for parse_summary.rows_processed display. Optional. */
  rows_parsed?: { balances: number; transactions: number };
  /** Soft errors the browser hit while parsing (missing optional column,
   *  malformed row that couldn't be coerced). Optional. */
  errors?: UploadParseError[];
}

export interface UploadFinalizeResponse {
  upload: Omit<UploadRow, 'parse_summary'>;
  parse_summary: UploadParseSummary;
}

export interface MissingAccountDecision {
  account_id: number;
  action: 'keep_tiller' | 'move_to_manual';
  reason?: string | null;
}

export interface UnmatchedTillerDecision {
  tiller_account_id: string;
  action: 'link' | 'ignore';
  /** required when action === 'link' */
  target_account_id?: number;
  /** captured for mapping_history.new_tiller_account_reference + audit */
  tiller_account_reference?: string | null;
  reason?: string | null;
}

export interface UploadResolutionsRequest {
  missing_account_decisions?: MissingAccountDecision[];
  unmatched_tiller_decisions?: UnmatchedTillerDecision[];
}

export interface UploadResolutionsResponse {
  upload_id: number;
  applied: {
    moved_to_manual: number;
    kept_as_tiller: number;
    linked: number;
    ignored: number;
  };
  resolutions_applied_at: ISO8601;
}

// ---- Track 2: Mapping View ----

export interface MappingRow {
  account_id: number;
  display_name: string;
  frt_customer_code: string;
  frt_customer_name: string;
  account_status: AccountStatus;
  tiller_status: TillerStatus;
  tiller_account_id: string | null;
  tiller_account_reference: string | null;
  last_seen_in_upload_id: number | null;
  last_seen_uploaded_at: ISO8601 | null;
}

export interface MappingListQuery {
  q?: string;
  tiller_status?: TillerStatus;
  account_status?: AccountStatus;
  has_tiller?: 'linked' | 'unlinked';
  sort?:
    | 'display_name'
    | 'frt_customer_code'
    | 'tiller_account_reference'
    | 'tiller_account_id'
    | 'last_seen_uploaded_at'
    | 'tiller_status';
  dir?: 'asc' | 'desc';
  page?: number;
  per_page?: number;
}

// ---- Track 6: Users (admin) ----

export interface UserListQuery {
  status?: UserStatus;
  role?: UserRole;
  page?: number;
  per_page?: number;
}

export type UserListItem = PublicUser;

export type UserListResponse = Paginated<UserListItem>;

export interface UserCreateRequest {
  username: string;
  email?: string | null;
  role: UserRole;
}

/**
 * Returned by POST /users. The temp password is shown ONCE here and never
 * stored anywhere else. The new user must change it on first login.
 */
export interface UserCreateResponse {
  user: PublicUser;
  /** 12-character random temp password, shown ONCE. */
  temp_password: string;
}

export interface UserPatchRequest {
  email?: string | null;
  role?: UserRole;
  status?: UserStatus;
  /** Toggle force-reset on next login. Set true to require reset; false clears the flag. */
  must_reset?: boolean;
}

export interface UserSuspendRequest {
  reason?: string | null;
}

// ---- Track 6: Audit Log ----

export interface AuditLogListQuery {
  actor_user_id?: number;
  entity_type?: string;
  entity_id?: string;
  action?: AuditAction;
  /** ISO date (YYYY-MM-DD) — events at >= this date */
  from?: DateOnly;
  /** ISO date (YYYY-MM-DD) — events strictly before this date + 1 day */
  to?: DateOnly;
  page?: number;
  per_page?: number;
}

export interface AuditLogListItem extends AuditLogRow {
  actor_username: string | null;
}

export type AuditLogListResponse = Paginated<AuditLogListItem>;

// ---- Track 6: Config ----

export interface AppConfig {
  /** Day of month at which the period closes (1-31). Default 28. */
  month_end_cutoff_day: number;
  /** Days since last upload before a Tiller account is "stale". Default 7. */
  stale_threshold_days: number;
  /** Days before cutoff that the manual entry window opens. Default 0. */
  manual_entry_window_days_before: number;
  /** Days after cutoff the window remains open. Default 5. */
  manual_entry_window_days_after: number;
  /** Grace days after cutoff before the pending banner fires. Default 0. */
  manual_entry_pending_grace_days: number;
  /** abs(amount) >= this threshold counts as "large". Default 500. */
  large_transaction_threshold: number;
  /** Default "hide within N days" on the dashboard stale-update widget. Default 3. */
  dashboard_stale_default_window_days: number;
}

export type AppConfigPatch = Partial<AppConfig>;

// ---- Track 6: Notifications ----

export interface NotificationListQuery {
  kind?: NotificationKind;
  /** 'unread' | 'read' | undefined (=all) */
  state?: 'unread' | 'read';
  page?: number;
  per_page?: number;
}

export interface NotificationListItem extends Omit<NotificationRow, 'payload'> {
  /** Parsed JSON payload (server decodes for FE convenience). */
  payload: Record<string, unknown> | null;
}

export type NotificationListResponse = Paginated<NotificationListItem>;

// ---- Track 4: Snapshots & Period Close ----

/**
 * Optional body for `POST /snapshots/:period/freeze`. The cutoff defaults to
 * the last day of the period at 23:59:59.999 UTC; callers may override it
 * (e.g. for late-close runs) by passing `cutoff_at` as an ISO-8601 timestamp.
 */
export interface SnapshotFreezeRequest {
  cutoff_at?: ISO8601;
}

/** Per-account preview row returned by `GET /snapshots/:period/preview`. */
export interface SnapshotPreviewItem {
  account_id: number;
  display_name: string;
  frt_customer_code: string;
  tester_display: string;
  account_type_code: string;
  product_type_code: string;
  tiller_status: TillerStatus;
  on_point_tester_display: string | null;
  /** Resolved latest balance with as_of_date <= cutoff_at; null when none. */
  balance_amount: number | null;
  balance_source: BalanceSource | null;
  balance_as_of: DateOnly | null;
  /** Days between the resolved balance date and the cutoff; null when balance missing. */
  balance_age_days: number | null;
  /** True when no balance exists for this account at or before cutoff_at. */
  missing_balance: boolean;
  /** True when an integrated account has not been seen in any upload yet. */
  stale_tiller: boolean;
  /** True if a snapshot already exists for this (account, period) pair. */
  already_frozen: boolean;
}

export interface SnapshotPreviewResponse {
  period: Period;
  cutoff_at: ISO8601;
  any_already_frozen: boolean;
  totals: {
    accounts_total: number;
    accounts_already_frozen: number;
    accounts_missing_balance: number;
    accounts_stale_tiller: number;
    balance_amount_sum: number;
  };
  items: SnapshotPreviewItem[];
}

export interface SnapshotFreezeResponse {
  period: Period;
  cutoff_at: ISO8601;
  frozen_count: number;
  /** Subset of frozen rows whose latest balance was missing — written with amount=0. */
  missing_balance_count: number;
  snapshots: MonthlySnapshotListItem[];
}

/** A snapshot row plus its parsed metadata blob and a joined display name. */
export interface MonthlySnapshotListItem {
  id: number;
  account_id: number;
  period: Period;
  balance_amount: number;
  balance_source: BalanceSource;
  metadata_snapshot: SnapshotMetadata;
  frozen_at: ISO8601;
  frozen_by_user_id: number;
  account_display_name: string;
}

export interface SnapshotAmendmentValue {
  /** JSON-deserialized values (string, number, boolean, null, object). */
  old_value: unknown;
  new_value: unknown;
  amended_by_user_id: number;
  amended_at: ISO8601;
  reason: string;
}

/**
 * Detail view: includes the immutable original blob, an overlay of the latest
 * amendment per field, and a `current` projection equal to original ⊕ overlay.
 * `current` is what reports/exports should consume.
 */
export interface MonthlySnapshotDetail extends MonthlySnapshotListItem {
  /** Map of field path -> latest amendment overlay. */
  amendments: Record<string, SnapshotAmendmentValue>;
  current: {
    balance_amount: number;
    balance_source: BalanceSource;
    metadata_snapshot: SnapshotMetadata;
  };
}

export interface SnapshotsForPeriodResponse {
  period: Period;
  data: MonthlySnapshotListItem[];
  totals: {
    count: number;
    balance_amount_sum: number;
  };
  /** ISO-8601 of the most-recent freeze; null when no snapshots exist for the period. */
  frozen_at: ISO8601 | null;
  frozen_by_user_id: number | null;
}

/**
 * Append-only amendment input. `field` is one of `balance_amount`,
 * `balance_source`, or a dotted metadata path like
 * `metadata_snapshot.tester_name`.
 */
export interface SnapshotAmendRequest {
  field: string;
  /** New value as a JSON-encodable scalar/object. Stored stringified. */
  new_value: unknown;
  reason: string;
}

export interface SnapshotAmendResponse {
  amendment: SnapshotAmendmentRow;
  detail: MonthlySnapshotDetail;
}

export interface SnapshotAmendmentsResponse {
  data: SnapshotAmendmentRow[];
}

// ---- Stubs for downstream tracks (still owned by other tracks) ----

// Track 5 owns the real shape; alias kept for backwards-compatibility with
// any Track 0 imports.
export type { ReportTemplateCreate as ReportTemplateUpsert } from './reports.js';

// ---- Manual Entry / Balances (Track 3) ----

/**
 * POST /balances request body. Exactly one of `as_of_date` or `period` must
 * be supplied. When `period` is supplied, the server resolves it to the
 * period's last calendar day. `period` is only allowed when source = manual
 * (single endpoint creates manual entries only).
 */
export interface BalanceCreate {
  account_id: number;
  as_of_date?: DateOnly;
  period?: Period;
  amount: number;
  notes?: string | null;
}

/** PATCH /balances/:id body — manual entries only, period must not be locked. */
export interface BalanceUpdate {
  amount?: number;
  as_of_date?: DateOnly;
  notes?: string | null;
}

/** POST /manual-entry/:period/bulk row. */
export interface ManualEntryBulkRow {
  account_id: number;
  amount: number;
  /** Optional YYYY-MM-DD; must fall within the target period. Defaults to
   *  the period's last day (legacy month-end semantics) when omitted. Used
   *  to capture an intra-month "current" balance that flows directly into
   *  Dashboard / report "Current" totals without dating it to month-end. */
  as_of_date?: string | null;
  notes?: string | null;
}

export interface ManualEntryBulkRequest {
  rows: ManualEntryBulkRow[];
}

export interface ManualEntryBulkResult {
  written: number;
  rows: Array<{ account_id: number; balance_id: number }>;
}

/** Returned per row from GET /manual-entry/:period. */
export type ManualEntryStatus = 'open' | 'submitted' | 'locked';

export interface ManualEntryRow {
  account_id: number;
  display_name: string;
  on_point_tester_name: string | null;
  frt_customer_code: string;
  status: ManualEntryStatus;
  /** Existing balance for this period if any; null when status === 'open' */
  balance: BalanceRow | null;
}

export interface ManualEntryView {
  period: Period;
  is_locked: boolean;
  rows: ManualEntryRow[];
}

// ---- Alerts (Track 3) ----

export interface StaleTillerRow {
  account_id: number;
  display_name: string;
  frt_customer_code: string;
  last_seen_in_upload_id: number | null;
  last_uploaded_at: string | null;
  days_stale: number | null;
}

export interface StaleUpdateRow {
  account_id: number;
  display_name: string;
  frt_customer_code: string;
  source: 'tiller' | 'manual' | null;
  last_balance_at: DateOnly | null;
  days_stale: number | null;
}

export interface ManualPendingRow {
  account_id: number;
  display_name: string;
  frt_customer_code: string;
  on_point_tester_name: string | null;
}

/** Account is tiller_status='integrated' but missing one or both of
 *  tiller_account_id / tiller_account_reference. Surfaces on the Dashboard
 *  as a data-integrity alert. */
export interface IntegratedWithoutTillerRow {
  account_id: number;
  display_name: string;
  frt_customer_code: string;
  tiller_account_id: string | null;
  tiller_account_reference: string | null;
  /** Names of the missing fields (one or both of the above). */
  missing: string[];
}

/** A Tiller account seen in any past upload that is not currently linked to
 *  any FRT account. Surfaces in the standalone Unmapped Tiller view so admins
 *  can resolve them outside the upload flow. */
export interface UnmappedTillerAccount {
  tiller_account_id: string;
  tiller_account_reference: string | null;
  account_number_last4: string | null;
  institution: string | null;
  latest_balance: number | null;
  latest_observed_at: ISO8601 | null;
  /** Most recent upload that surfaced this Tiller account. */
  last_seen_upload_id: number | null;
  last_seen_uploaded_at: ISO8601 | null;
}

// ---------------------------------------------------------------------------
// Hard-delete options (mig 0010 + ops feedback 2026-04-29).
// 'full'              → cascade-delete every dependent row (test scenarios)
// 'preserve_history'  → cascade for live state, KEEP frozen monthly_snapshots
//                       and audit_log entries (most defensible default)
// ---------------------------------------------------------------------------
export type HardDeleteMode = 'full' | 'preserve_history';

export interface HardDeleteRequest {
  mode: HardDeleteMode;
  reason?: string | null;
}

export interface HardDeleteResponse {
  deleted: {
    self: number;
    balance: number;
    transaction: number;
    monthly_snapshot: number;
    snapshot_amendment: number;
    tiller_mapping_history: number;
    account_status_event: number;
    audit_log: number;
    account: number;
    tester: number;
    frt_customer: number;
  };
}

// ---------------------------------------------------------------------------
// Tester Compensation page (mig 0010).
// One row per (tester, account). Editable: payment_status, payment_date.
// ---------------------------------------------------------------------------

export interface TesterCompensationRow {
  tester_id: number;
  tester_display: string;
  tester_category: TesterCategory | null;
  tester_status_name: string | null;
  account_id: number;
  account_display_name: string;
  current_balance: number | null;
  current_balance_as_of: DateOnly | null;
  contract_signed: boolean | null;
  contract_signed_date: DateOnly | null;
  contract_term_months: number | null;
  contract_expiration_date: DateOnly | null;
  frt_tester_fee: number | null;
  payment_status_id: number | null;
  payment_status_name: string | null;
  payment_date: DateOnly | null;
  needs_renewal: boolean;
  /** Convenience flag: true when needs_renewal=1 OR payment_status='Closing'
   *  OR payment_status='Suspended'. Drives badges in the UI. */
  attention_state: 'action_required' | 'pending_closing' | 'suspended' | null;
}

export interface TesterCompensationResponse {
  data: TesterCompensationRow[];
}

/** PATCH the payment status (and optionally payment_date) on an account.
 *  payment_date REQUIRED when payment_status_id resolves to "Paid". */
export interface AccountPaymentStatusPatch {
  payment_status_id: number | null;
  payment_date?: DateOnly | null;
}

/** Body for POST /accounts/:id/contract-renewal — invoked from the
 *  Action Required modal. Resolves the needs_renewal flag. */
export interface AccountContractRenewalRequest {
  decision: 'renew' | 'close' | 'suspend';
  // Required when decision = 'renew'
  contract_signed?: boolean;
  contract_signed_date?: DateOnly;
  contract_term_months?: number;
  frt_tester_fee?: number | null;
  payment_status_id?: number | null;
}

// ---- Account notes (mig 0016) ----

export const ACCOUNT_NOTE_MAX = 4000;

/** One entry in an account's Note History (append-only). */
export interface AccountNote {
  id: number;
  account_id: number;
  body: string;
  created_by_user_id: number | null;
  created_by_username: string | null;
  created_at: ISO8601;
}

export interface AccountNoteCreate {
  body: string;
}
